import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SwitchPlain } from "~/components/formElements/Switch/Switch.tsx";
import Table from "~/components/Table";
import { INVOICE_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { INVOICES_PROCESSED_PAGE_KEY, INVOICES_SHARED_FILTER_VALUES } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import {
	applyDeliveryPeriodFilter,
	castFilterDatesToDate,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import InvoicePageHeader from "~/modules/billing/components/InvoicePageHeader";
import DownloadProcessedInvoicesTableDataButton
	from "~/modules/billing/components/ProcessedInvoicesIndexView/components/DownloadProcessedInvoicesTableDataButton";
import {
	processedInvoicesIndexTableColumnDefs,
} from "~/modules/billing/components/ProcessedInvoicesIndexView/processedInvoiceIndexTableColumnDefs.tsx";
import type {
	InvoiceIndexTableData,
} from "~/modules/billing/components/UnpaidInvoicesIndexView/unpaidInvoicesIndexTableTypes.ts";
import {
	InvoicePageTabId,
	invoiceTablesDefaultPageState,
	invoiceTableSharedFilterDefaultPageState,
} from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";

type Props = {
	isLoading: boolean;
	tableData: InvoiceIndexTableData[];
};

const processedInvoicesPageState = { ...invoiceTablesDefaultPageState, showUnpaidInvoices: true };

const ProcessedInvoicesIndexView: FunctionComponent<Props> = ({ isLoading, tableData }) => {
	const {hasAnyPermission} = useAuth();
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: INVOICES_PROCESSED_PAGE_KEY,
		defaultState: processedInvoicesPageState,
	});

	const { pageState: sharedFilterPageState, setPageState: setSharedFilterPageState } = usePageStateStorage({
		pageKey: INVOICES_SHARED_FILTER_VALUES,
		defaultState: invoiceTableSharedFilterDefaultPageState,
	});
	const [showUnpaidInvoices, setShowUnpaidInvoices] = useState<boolean>(pageState.showUnpaidInvoices);
	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRangeNullable>(castFilterDatesToDate(sharedFilterPageState.deliveryPeriodFilter));
	const [globalFilter, setGlobalFilter] = useState<string>(sharedFilterPageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState({ sorting, showUnpaidInvoices });
	}, [setPageState, showUnpaidInvoices, sorting]);

	useEffect(() => {
		setSharedFilterPageState({ globalFilter, deliveryPeriodFilter });
	}, [deliveryPeriodFilter, globalFilter, setSharedFilterPageState]);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const navigate = useNavigate();

	const preFilteredTableData = useMemo(() => {
		const withDeliveryPeriodFilter = applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);
		if (!showUnpaidInvoices) {
			const paidInvoiceStatuses: InvoiceStatusId[] = [invoiceStatusId.Paid, invoiceStatusId.Reversed];
			return withDeliveryPeriodFilter.filter((invoice) => paidInvoiceStatuses.includes(invoice.invoiceStatusId));
		}
		return withDeliveryPeriodFilter;
	}, [deliveryPeriodFilter, tableData, showUnpaidInvoices]);

	const handleRowClick = useCallback(
		(row: Row<InvoiceIndexTableData>, event: MouseEvent) => {
			const path = INVOICE_DETAILS_PATH_WITH_ORIGIN(row.original.id, validInvoiceDetailsNavigatedFromOrigins.PROCESSED_INVOICES_INDEX);

			if (event.ctrlKey || event.metaKey) {
				window.open(path, "_blank");
			} else {
				navigate(path);
			}
		},
		[navigate],
	);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: processedInvoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	const { rows } = table.getRowModel();

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollContainerRef.current,
		estimateSize: () => 48.5, // Adjust based on your row height (px-4 py-3 ≈ 60px)
		overscan: 10,
	});

	return <div className={clsx("auto-grid-2-rows mx-auto h-screen px-8")}>
		<InvoicePageHeader activeTabId={InvoicePageTabId.PROCESSED}
						   deliveryPeriodFilter={deliveryPeriodFilter}
						   globalFilter={globalFilter}
						   setDeliveryPeriodFilter={setDeliveryPeriodFilter}
						   setGlobalFilter={setGlobalFilter}
						   userCanManageInvoices={userCanManageInvoices}

		>
			<SwitchPlain onChange={(value)=>setShowUnpaidInvoices(!value)}
						 checked={!showUnpaidInvoices}
						 label="Unbezahlte Rechnungen verbergen" />
			<DownloadProcessedInvoicesTableDataButton deliveryPeriodFilter={deliveryPeriodFilter}
													  filteredTableData={table.getFilteredRowModel().rows.map(row => row.original)}
													  search={globalFilter} />
		</InvoicePageHeader>

		<div className="h-full overflow-hidden pb-10">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true}
				   virtualized={true}
				   scrollContainerRef={scrollContainerRef}
				   virtualizer={virtualizer} />
		</div>
	</div>;

};

export default ProcessedInvoicesIndexView;